import classNames from "classnames";
import qs from "qs";
import * as React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-yup";
import * as Yup from "yup";

import {
  OpportunitySearchQueryState,
  useAppSettings,
  useRootViewDispatch,
} from "@skv/state";

import { ExternalLink, useAnalyticsUtil } from "@skv/core";
import { Alert } from "react-bootstrap";
import { useUserDispatch, useUserState } from "~/state";
import { SplitField } from ".";

interface OpportunityEmailSubscribeFormProps {
  id: string;
  className?: string;
  searchQuery: OpportunitySearchQueryState;
}

const OpportunityEmailSubscribeForm: React.FC<
  OpportunityEmailSubscribeFormProps
> = ({ id, className, searchQuery }) => {
  const [success, setSuccess] = React.useState(false);

  const [error, setError] = React.useState(null);

  const { trackPageLink } = useAnalyticsUtil();

  const { seekVolunteerUrl } = useAppSettings();

  const {
    createSubmitHandler,
    field,
    errors,
    touched,
    values,
    isSubmitting,
    FormProvider,
  } = useForm<{
    email: string;
  }>({
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email address is needed to subscribe"),
    }),
  });

  const onSubmit = React.useMemo(() => {
    return createSubmitHandler((data) => {
      const queryString = qs.stringify(searchQuery, {
        arrayFormat: "repeat",
      });

      return fetch("/api/volunteering/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        referrer: `/volunteering?${queryString}`,
        body: JSON.stringify(data),
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Unhandled error");
        })
        .then(({ data, errorCode, errorMessage, success }) => {
          if (success) {
            setSuccess(true);
            trackPageLink("subscribe-to-new-opportunity-email");
          }

          switch (errorCode) {
            case "error:subscription:alreadyexists":
              setSuccess(true); // User doesn't care if they're already subscribed so show success
              break;
            default:
              setError(errorCode);
              break;
          }
        })
        .catch((err) => {
          setError("error:emailsub:unhandled");
        });
    });
  }, [createSubmitHandler, searchQuery, trackPageLink]);

  return (
    <FormProvider>
      <form
        className={classNames("p-4 bg-gray-200 mb-4", className)}
        action="/volunteering/subscribe"
        method="POST"
        onSubmit={onSubmit}
        aria-label="Save Search, subscribe to opportunities"
      >
        {((): React.ReactNode => {
          if (success) {
            return (
              <div className="text-center">
                <h2 className="mb-1">You have saved this search!</h2>
                <p className="mb-0">
                  Look out for new opportunities in your email soon.
                </p>
              </div>
            );
          }

          if (error) {
            return (
              <Alert
                show
                variant="danger"
                children={
                  <>
                    <h2 className="mb-1">
                      Sorry, we could not subscribe you at this time.
                    </h2>
                    <span>
                      If you&apos;re still stuck, let us know by{" "}
                      <a
                        href={seekVolunteerUrl + "/help-and-support/contact-us"}
                      >
                        contacting us
                      </a>
                      .
                    </span>
                  </>
                }
              />
            );
          }

          return (
            <>
              <h2 className="mb-1">
                Save this search and receive new opportunities by email.
              </h2>
              <p className="mb-0">
                When new opportunities for this search are posted, we'll send
                you an email.
              </p>
              <p className="small">
                By saving, you agree to our{" "}
                <ExternalLink
                  href={seekVolunteerUrl + "/legal/privacy-statement"}
                >
                  Privacy Policy
                </ExternalLink>{" "}
                and to receive messages about volunteer opportunities and other
                updates about products and features from SEEK Volunteer. You can
                unsubscribe at any time.
              </p>
              <SplitField
                {...field}
                id={`${id}-email`}
                label="Email address"
                name="email"
                type="email"
                autoComplete="email"
                value={values.email || ""}
                touched={!!touched.email}
                error={errors.email}
                append={
                  <button
                    className="flex-shrink-0 btn btn-primary"
                    css={`
                      min-width: 80px;
                    `}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving" : "Save"}
                  </button>
                }
              />
            </>
          );
        })()}
      </form>
    </FormProvider>
  );
};

interface AuthAuthenticatedOpportunityEmailSubscribeFormProps
  extends OpportunityEmailSubscribeFormProps {
  email: string;
}

/**
 * TODO:
 * - get working via server post too?
 * - Default state, should be persisted across searches based on searchQuery?
 *  */
const AuthenticatedOpportunityEmailSubscribeForm: React.FC<
  AuthAuthenticatedOpportunityEmailSubscribeFormProps
> = ({ className, searchQuery, email }) => {
  const { updateUser } = useUserDispatch();
  const dispatch = useRootViewDispatch();

  const { trackPageLink } = useAnalyticsUtil();

  const { seekVolunteerUrl } = useAppSettings();

  enum Status {
    "SUBSCRIBED",
    "NOT_SUBSCRIBED",
  }

  const [state, setState] = React.useState(() => {
    /* TODO : Default state, should be persisted across searches based on searchQuery? */
    return {
      status: Status.NOT_SUBSCRIBED,
      loading: false,
      error: null,
    };
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    setState((prev) => ({
      ...prev,
      loading: true,
    }));

    const queryString = qs.stringify(searchQuery, {
      arrayFormat: "repeat",
    });

    return fetch("/api/volunteering/subscribe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      referrer: `/volunteering?${queryString}`,
      body: JSON.stringify({
        email,
      }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }

        throw Error("Unhandled error");
      })
      .then(({ data, errorCode, errorMessage, success }) => {
        if (success) {
          setState((prev) => ({
            ...prev,
            loading: false,
            status: Status.SUBSCRIBED,
          }));

          updateUser((prev) => ({
            ...prev,
            emailAlertCount: prev.emailAlertCount + 1,
          }));

          // invalidate /my-activity/subscriptions
          dispatch({
            type: "REMOVE_CACHED_VIEW",
            payload: {
              url: "/my-activity/subscriptions",
            },
          });

          trackPageLink("subscribe-to-new-opportunity-email");

          return;
        }

        switch (errorCode) {
          case "error:subscription:alreadyexists":
            setState((prev) => ({
              ...prev,
              loading: false,
              status: Status.SUBSCRIBED,
            }));
            break;
          default:
            setState((prev) => ({
              ...prev,
              loading: false,
              error: errorCode,
            }));
            break;
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "error:emailsub:unhandled",
        }));
      });
  };

  return (
    <form
      className={classNames("p-4 bg-light shadow-sm border mb-4", className)}
      action="/volunteering/subscribe"
      method="POST"
      onSubmit={handleSubmit}
      aria-label="Save Search, subscribe to opportunities"
    >
      {((): React.ReactNode => {
        switch (state.status) {
          case Status.SUBSCRIBED:
            return (
              <div className="text-center">
                <p>
                  <strong>You have saved this search!</strong>
                </p>
                <p className="mb-0">
                  Go to{" "}
                  <Link
                    className="strong"
                    to={(loc) => ({
                      pathname: "/my-activity/subscriptions",
                      state: loc.state,
                    })}
                  >
                    Email alerts
                  </Link>{" "}
                  to see and manage all your saved searches.
                </p>
              </div>
            );
          case Status.NOT_SUBSCRIBED:
          default:
            return (
              <div className="d-md-flex mx-n2">
                <div className="px-2 flex-grow-1 mb-3 mb-md-0">
                  <h2 className="mb-1">
                    Save this search and receive new opportunities by email.
                  </h2>
                  <p className="mb-0">
                    When new opportunities for this search are posted, we'll
                    send an email.
                  </p>
                  <p className="small">
                    By saving, you agree to our{" "}
                    <ExternalLink
                      href={seekVolunteerUrl + "/legal/privacy-statement"}
                    >
                      Privacy Policy
                    </ExternalLink>{" "}
                    and to receive messages about volunteer opportunities and
                    other updates about products and features from SEEK
                    Volunteer. You can unsubscribe at any time.
                  </p>
                </div>
                <div className="px-2">
                  <button
                    className="btn btn-secondary"
                    css={`
                      min-width: 80px;
                    `}
                    disabled={state.loading}
                  >
                    {state.loading ? "Waiting..." : "Save"}
                  </button>
                </div>
              </div>
            );
        }
      })()}
    </form>
  );
};

export const EmailSubscribeForm = (
  props: OpportunityEmailSubscribeFormProps
) => {
  const { isAuthenticated, email } = useUserState();

  if (isAuthenticated && email) {
    return (
      <AuthenticatedOpportunityEmailSubscribeForm {...props} email={email} />
    );
  }

  return <OpportunityEmailSubscribeForm {...props} />;
};
